/**
 * GuestLanding — the post-share entry page at /e/:slug.
 *
 * This is the conversion surface: host shared the link, guest lands
 * here, and we must do 3 things in order:
 *
 *   1. Zero-friction upload (no auth, no signup, no form) — this is
 *      the primary action. If it's slow, the loop breaks.
 *   2. Emotional payoff + live gallery glimpse — "your photo is now
 *      part of {event_name}'s story"
 *   3. Soft host-conversion CTA — surfaces ONLY after the guest
 *      uploads or after 15s of browsing. Never on first load.
 *
 * Distinct from /event/:slug/guest (GuestHome) which is the
 * QR-experience for already-invited guests with purchased features.
 */
import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { UploadSimple, Camera, Sparkle, CaretRight, CheckCircle, Envelope, X, Bell, BookOpen, Microphone, FilmStrip, Play, Lightning, ImageSquare } from '@phosphor-icons/react';
import { EventDropLogo } from '../components/EventDropLogo';
import { trackGrowth, getAnonId } from '../lib/growthTrack';
import { isPushSupported, pushPermission, requestAndSubscribeGuest } from '../lib/push';
import { buildTabs, FEATURE_TAB_MAP, TAB_VALUE_LABEL, FEATURE_CARDS, activateFeature, shouldShowNewPip, markFeatureSeen, markFeatureComplete, completionStats, useEngagement, engagementCounterText } from '../lib/features';
import { EventLivePills } from '../components/EventLivePills';
import CompletionBar from '../components/CompletionBar';
import CompletionShareSheet from '../components/CompletionShareSheet';
import SupplierBrowseLayer, { SoftMarketplaceCTA } from '../components/SupplierBrowseLayer';
import Year6TeamPicker from '../components/Year6TeamPicker';
import { useEventRecovery, useEventSession } from '../context/EventSessionProvider';
import { fetchOnce } from '../lib/eventCache';
import ReconnectingScreen from '../components/ReconnectingScreen';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Icons consumed by FEATURE_CARDS — resolved from string names so the
// shared lib/features.js stays icon-library-agnostic.
const ICON_MAP = { BookOpen, Microphone, Camera, Lightning, Play, FilmStrip, ImageSquare };

const TAB_META = {
  upload:     { label: 'Upload',     Icon: UploadSimple },
  guestbook:  { label: 'Leave a memory',  Icon: BookOpen },
  video:      { label: 'Video',      Icon: Microphone },
  booth:      { label: 'Booth',      Icon: Camera },
  challenges: { label: 'Challenges', Icon: Lightning },
  highlights: { label: 'Highlights', Icon: FilmStrip },
};

const EVENT_TYPE_META = {
  wedding:    { emoji: '💍' },
  birthday:   { emoji: '🎉' },
  corporate:  { emoji: '🏢' },
  kids_party: { emoji: '🎈' },
  other:      { emoji: '🎯' },
};

function inferEventType(name) {
  const n = (name || '').toLowerCase();
  if (n.includes('wedding') || n.includes('engagement') || n.includes('anniversary')) return 'wedding';
  if (n.includes('birthday') || n.includes('bday')) return 'birthday';
  if (n.includes('corporate') || n.includes('company') || n.includes('gala') || n.includes('launch')) return 'corporate';
  if (n.includes('kids') || n.includes("kid's") || n.includes('children')) return 'kids_party';
  return 'other';
}

const GuestLanding = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const revealEntryFiredRef = useRef(false);
  const { event: recoveredEvent, status: recoveryStatus, retry: retryRecovery } = useEventRecovery(slug);
  const { activeEvent } = useEventSession();
  const [event, setEvent] = useState(null);
  const { counts: engagementCounts } = useEngagement(event?.event_id, axios, API);
  const notFound = recoveryStatus === 'failed';
  const [photos, setPhotos] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploaded, setUploaded] = useState(false); // flipped on first successful upload
  const [showHostCta, setShowHostCta] = useState(false);
  const [guestName, setGuestName] = useState(() => localStorage.getItem(`guest_name_${slug}`) || '');
  const [showNameFld, setShowNameFld] = useState(false);
  const [uploadsOnDevice, setUploadsOnDevice] = useState(() => Number(localStorage.getItem(`upload_ct_${slug}`) || 0));
  const [visitsOnDevice] = useState(() => {
    try {
      const k = `visit_ct_${slug}`;
      const n = Number(localStorage.getItem(k) || 0) + 1;
      localStorage.setItem(k, String(n));
      return n;
    } catch { return 1; }
  });
  const [showKeepMoments, setShowKeepMoments] = useState(false);
  const [captured, setCaptured] = useState(() => !!localStorage.getItem(`captured_${slug}`));
  const [dismissedKeep, setDismissedKeep] = useState(false);
  const [showPushAsk, setShowPushAsk] = useState(false);
  const [pushDismissed, setPushDismissed] = useState(() => !!localStorage.getItem(`push_ask_dismissed_${slug}`));
  const [liveGuestCount, setLiveGuestCount] = useState(0);
  const [browseOpen, setBrowseOpen] = useState(() => searchParams.get('browse') === '1');
  const fileRef = useRef(null);

  // URL ↔ browse-layer sync. Keeping this in the URL means the back
  // button closes the sheet instead of leaving the event (critical
  // for the "didn't navigate, just went deeper" feel).
  useEffect(() => { setBrowseOpen(searchParams.get('browse') === '1'); }, [searchParams]);
  const openBrowse = () => {
    const next = new URLSearchParams(searchParams);
    next.set('browse', '1');
    setSearchParams(next);
  };
  const closeBrowse = () => {
    const next = new URLSearchParams(searchParams);
    next.delete('browse');
    setSearchParams(next);
  };

  // Tab engine — driven entirely by event.features[] from the backend.
  const tabs = useMemo(() => (event ? buildTabs(event.features) : ['upload']), [event]);
  const [activeTab, setActiveTab] = useState('upload');
  const activatedRef = useRef(new Set()); // local dedupe so we only
  // fire /activate-feature once per feature per page session (the
  // backend is idempotent too; this just trims needless requests).

  // Activate the underlying feature(s) whenever a tab is opened. Fire-
  // and-forget so tab switching stays snappy; the server has already
  // pre-activated for paid packages so this is near-always a no-op on
  // the wire. We call for every feature that maps to this tab (e.g.
  // "booth" tab covers both pocket_booth + photo_booth).
  useEffect(() => {
    if (!event?.event_id || !event?.features?.length) return;
    const wanted = event.features.filter((f) => FEATURE_TAB_MAP[f] === activeTab);
    wanted.forEach((f) => activateFeature(axios, API, event.event_id, f, activatedRef.current));
  }, [activeTab, event?.event_id, event?.features]);

  // 1. Wire the EventSessionProvider recovery hook → local state + photos.
  // The hook handles the tiered retry/persistence — we just react.
  useEffect(() => {
    if (recoveryStatus !== 'ready' || !recoveredEvent?.event_id) return;
    setEvent(recoveredEvent);
    const eid = recoveredEvent.event_id;
    // fetchOnce — shares the prefetcher's in-flight promise OR returns
    // a 60s-fresh cached value. No duplicate round-trips.
    fetchOnce(`${eid}:photos`, () => axios.get(`${API}/events/${eid}/photos`).then((r) => r.data))
      .then((photos) => setPhotos((photos || []).slice(0, 12)))
      .catch(() => {});
    fetchOnce(`${eid}:public-stats`, () => axios.get(`${API}/events/${eid}/public-stats`).then((r) => r.data), { ttl: 30_000 })
      .then((stats) => setLiveGuestCount(Number(stats?.guests || 0)))
      .catch(() => {});
    // Growth telemetry — fire once per mount, backend dedupes by anon_id+day
    trackGrowth('guest_visit', { event_id: eid });
    if (searchParams.get('reveal') === '1' && !revealEntryFiredRef.current) {
      revealEntryFiredRef.current = true;
      trackGrowth('reveal_phone_entry', { event_id: eid, metadata: { slug } });
    }
  }, [recoveryStatus, recoveredEvent, slug, searchParams]);

  // Keep Your Moments trigger: fires when guest has earned it
  // (≥3 uploads OR ≥2 visits + 1 upload), not on first load
  useEffect(() => {
    if (captured || dismissedKeep) return;
    if (uploadsOnDevice >= 3 || (visitsOnDevice >= 2 && uploadsOnDevice >= 1)) {
      setShowKeepMoments(true);
    }
  }, [uploadsOnDevice, visitsOnDevice, captured, dismissedKeep]);

  // 2. Soft host-conversion CTA — 15s dwell trigger (never on first load)
  useEffect(() => {
    if (uploaded) return; // once uploaded, we surface it via the success state instead
    const t = setTimeout(() => setShowHostCta(true), 15000);
    return () => clearTimeout(t);
  }, [uploaded]);

  // Push soft-ask — fires 4s after first successful upload (peak
  // engagement moment). Respects 14-day dismissal + current permission.
  // Suppressed while the Keep Your Moments sheet is showing to avoid
  // overlapping surfaces.
  useEffect(() => {
    if (!uploaded || pushDismissed) return;
    if (showKeepMoments) return;
    if (!isPushSupported()) return;
    if (pushPermission() === 'granted' || pushPermission() === 'denied') return;
    const t = setTimeout(() => setShowPushAsk(true), 4000);
    return () => clearTimeout(t);
  }, [uploaded, pushDismissed, showKeepMoments]);

  const handlePushAsk = async () => {
    const r = await requestAndSubscribeGuest({
      anon_id: getAnonId(),
      event_id: event?.event_id,
    });
    if (r?.ok) toast.success("We'll let you know when new photos arrive 🔔");
    else if (r?.reason !== 'denied') toast.error("Couldn't set up notifications");
    setShowPushAsk(false);
    try { localStorage.setItem(`push_ask_dismissed_${slug}`, '1'); } catch {}
    setPushDismissed(true);
  };

  const dismissPushAsk = () => {
    setShowPushAsk(false);
    try { localStorage.setItem(`push_ask_dismissed_${slug}`, '1'); } catch {}
    setPushDismissed(true);
  };

  // 3. Poll photos every 15s so the live gallery feels alive
  useEffect(() => {
    if (!event?.event_id) return;
    const t = setInterval(async () => {
      try {
        const p = await axios.get(`${API}/events/${event.event_id}/photos`);
        setPhotos((p.data || []).slice(0, 12));
      } catch {}
    }, 15000);
    return () => clearInterval(t);
  }, [event?.event_id]);

  const handleUpload = useCallback(async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length || !event) return;
    setUploading(true);
    let ok = 0, fail = 0;
    try {
      const CONCURRENCY = 3;
      for (let i = 0; i < files.length; i += CONCURRENCY) {
        const batch = files.slice(i, i + CONCURRENCY);
        const results = await Promise.allSettled(batch.map((file) => {
          const fd = new FormData();
          fd.append('file', file);
          if (guestName) fd.append('guest_name', guestName);
          return axios.post(`${API}/events/${event.event_id}/photos?source=share_link`, fd, {
            headers: { 'Content-Type': 'multipart/form-data' },
            timeout: 120000,
          });
        }));
        results.forEach((r) => { if (r.status === 'fulfilled') ok++; else fail++; });
      }
      if (ok) {
        setUploaded(true);
        setShowHostCta(true); // Primary conversion moment
        // Completion: any successful upload counts as the upload
        // experience completed. Fires the dopamine toast once + a
        // window event so the CompletionBar tick re-renders.
        const c = markFeatureComplete(slug, 'upload');
        if (!c.already) {
          try {
            window.dispatchEvent(new CustomEvent('eventdrop:completion_tick'));
            // Micro-celebration only when there's >1 thing to do AND
            // we're not at the final completion (CompletionBar handles
            // the "all done" state by hiding itself).
            if ((event?.features || []).length > 1) {
              toast.success(`✨ ${c.count} experience${c.count === 1 ? '' : 's'} unlocked`, { duration: 2200 });
            }
          } catch {}
        }
        try {
          const newCt = uploadsOnDevice + ok;
          localStorage.setItem(`upload_ct_${slug}`, String(newCt));
          setUploadsOnDevice(newCt);
        } catch {}
        // Refresh gallery so the new upload shows up immediately
        try {
          const p = await axios.get(`${API}/events/${event.event_id}/photos`);
          setPhotos((p.data || []).slice(0, 12));
        } catch {}
      }
      if (ok && !fail) toast.success(ok === 1 ? 'Uploaded 🎉' : `${ok} uploaded 🎉`);
      else if (ok && fail) toast.warning(`${ok} uploaded, ${fail} failed`);
      else toast.error('Upload failed — try again?');
    } catch {
      toast.error('Upload failed');
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  }, [event, guestName, slug, uploadsOnDevice]);

  const saveGuestName = (n) => {
    const v = n.trim().slice(0, 40);
    setGuestName(v);
    if (v) localStorage.setItem(`guest_name_${slug}`, v);
    else localStorage.removeItem(`guest_name_${slug}`);
    setShowNameFld(false);
  };

  if (notFound) {
    return <ReconnectingScreen status="failed" onRetry={retryRecovery} dashboardHref="/" slug={slug} activeEvent={activeEvent} />;
  }
  if (!event) {
    return <ReconnectingScreen status={recoveryStatus === 'restoring' ? 'restoring' : 'reconnecting'} slug={slug} activeEvent={activeEvent} />;
  }

  const etype = event.event_type || inferEventType(event.event_name);
  const emoji = (EVENT_TYPE_META[etype] || EVENT_TYPE_META.other).emoji;
  const humanDate = event.event_date ? new Date(event.event_date).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' }) : '';

  return (
    <div className="min-h-screen text-white px-5 py-8" style={{ background: 'radial-gradient(circle at top, #1a1a2e, #0F1014)' }} data-testid="guest-landing">
      <Year6TeamPicker event={event} />
      {/* HERO */}
      <div className="max-w-md mx-auto text-center mb-6">
        <EventDropLogo size={32} className="mx-auto mb-3 opacity-60" />
        <p className="text-[10px] font-black tracking-[2.5px] uppercase text-[#C29D52]/85 mb-1" data-testid="hero-invite-line">You're invited</p>
        <h1 className="text-3xl sm:text-4xl font-black leading-tight mb-2 flex items-center justify-center gap-2" data-testid="guest-event-name">
          <span>{event.event_name}</span>
          <span>{emoji}</span>
        </h1>
        <p className="text-white/50 text-sm">
          {humanDate}
          {event.event_location ? ` · 📍 ${event.event_location}` : (event.event_postcode ? ` · 📍 ${event.event_postcode}` : '')}
        </p>
      </div>

      {/* COMPLETION BAR — only renders once the guest has interacted
          (1+ feature complete). Driven by the shared lib/features
          completion store; survives navigation across feature pages. */}
      <CompletionBar
        slug={slug}
        features={event.features || []}
        onTabHint={(tab) => setActiveTab(tab)}
      />

      {/* PEAK-MOMENT SHARE TRIGGER — fires exactly once when the guest
          has completed every experience in the package. Bottom sheet
          (not a toast — this moment deserves more weight). */}
      <CompletionShareSheet
        slug={slug}
        features={event.features || []}
        eventName={event.event_name}
        eventId={event.event_id}
      />

      {/* LIVE CONTRIBUTION COUNTER — immediate social proof on arrival
          for shared-link visitors. Hidden below 2 (weak signal), capped
          at 99+ (keeps the pill tight). No polling. Prom audiences
          respond strongly to "X students" framing, so we swap copy
          when the event is type=prom. */}
      {liveGuestCount >= 2 && (
        <div className="max-w-md mx-auto mb-4 text-center" data-testid="guest-activity-banner">
          <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11px] font-black border" style={{ background: 'rgba(255, 107, 53, 0.08)', color: '#FFB88A', borderColor: 'rgba(255, 107, 53, 0.25)' }}>
            🔥 <span className="tabular-nums">{liveGuestCount > 99 ? '99+' : liveGuestCount}</span>
            {event.type === 'prom' ? ' students already added memories' : ' guests already added memories'}
          </span>
        </div>
      )}

      {/* POCKET BOOTH — Year 6 only. Top-of-page CTA so kids see it first.
          Behaviour engine: more captures → more content → £99 conversion.
          Warm purple→pink→amber gradient (matches the homepage card). */}
      {event.type === 'year6' && (
        <div className="max-w-md mx-auto mb-5" data-testid="guest-pocket-booth-card">
          <button
            onClick={() => navigate(`/year6/${slug}/booth`)}
            className="w-full rounded-2xl p-[1.5px] block text-left transition-transform active:scale-[0.99]"
            style={{ background: 'linear-gradient(135deg, #8B5CF6 0%, #EC4899 50%, #FACC15 100%)' }}
            data-testid="guest-pocket-booth-cta"
          >
            <div className="rounded-2xl bg-[#0F1014] px-4 py-3.5 flex items-center gap-3">
              <div className="text-3xl shrink-0" aria-hidden="true">📸</div>
              <div className="min-w-0 flex-1">
                <p className="text-[10px] font-black uppercase tracking-[0.18em] text-[#C29D52] mb-0.5">Pocket Booth</p>
                <p className="text-white text-sm font-black mb-0.5 truncate">Snap photos with Year 6 overlays</p>
                <p className="text-white/55 text-[11px]">Tap to capture · saves straight to your Memory Book</p>
              </div>
              <div
                className="shrink-0 text-black text-xs font-black px-3 py-2 rounded-xl"
                style={{ background: 'linear-gradient(135deg, #8B5CF6 0%, #EC4899 50%, #FACC15 100%)' }}
              >
                Open →
              </div>
            </div>
          </button>
        </div>
      )}

      {/* EXPERIENCE VALUE STRIP — "This event includes…" — hidden when
          the event only has upload (nothing to brag about there). */}
      {tabs.length > 1 && (
        <div className="max-w-md mx-auto mb-4 text-center" data-testid="guest-experience-strip">
          <p className="text-[10px] font-black tracking-[2.5px] uppercase text-[#C29D52]/85 mb-1.5">✨ This event includes</p>
          <p className="text-white/60 text-xs">
            {tabs.map((t) => TAB_VALUE_LABEL[t]).filter(Boolean).join(' · ')}
          </p>
        </div>
      )}

      {/* TAB SWITCHER replaced (Feb 2026) with stacked dashboard-style
          launch buttons — gives every owned feature its own prominent
          gradient button so guests immediately see what's available
          (Spin Film 360, Memory Book, Video Guestbook, etc.) instead
          of hunting through small pill tabs. */}
      {event.features && event.features.length > 0 && (
        <div className="max-w-md mx-auto mb-5" data-testid="guest-feature-launchers">
          <EventLivePills counts={engagementCounts} testId="guest-landing-live-pills" />
          <h3
            data-testid="guest-do-header"
            className="text-white/95 font-black mb-3"
            style={{ fontSize: 22, letterSpacing: '-0.02em', fontFamily: 'Georgia, "Times New Roman", serif' }}
          >
            What should I do?
          </h3>
          <div className="space-y-3">
          {event.features
            .filter((f) => FEATURE_CARDS[f])
            .sort((a, b) => {
              const order = ['upload', 'guestbook', 'video', 'spin360', 'memory_snap', 'booth', 'challenges', 'highlights'];
              const ai = order.indexOf(FEATURE_TAB_MAP[a] || '');
              const bi = order.indexOf(FEATURE_TAB_MAP[b] || '');
              return (ai === -1 ? 99 : ai) - (bi === -1 ? 99 : bi);
            })
            .map((feature) => {
              const card = FEATURE_CARDS[feature];
              const Icon = ICON_MAP[card.iconName] || Camera;
              const isNew = shouldShowNewPip(slug, feature);
              const counterText = engagementCounterText(feature, engagementCounts);
              return (
                <button
                  key={feature}
                  onClick={() => {
                    markFeatureSeen(slug, feature);
                    activateFeature(axios, API, event.event_id, feature, activatedRef.current);
                    navigate(card.route(slug));
                  }}
                  className="relative w-full flex items-center gap-3 px-5 py-4 rounded-2xl text-left text-white transition-all hover:scale-[1.01] active:scale-[0.98]"
                  style={{ background: card.gradient, boxShadow: '0 8px 24px rgba(0,0,0,0.25)' }}
                  data-testid={`guest-launch-${feature}`}
                >
                  <Icon size={22} weight="bold" className="flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <span className="block font-black text-base" style={{ letterSpacing: '-0.01em' }}>{card.cta}</span>
                    {counterText && (
                      <span
                        className="block text-[11px] font-semibold mt-0.5 truncate"
                        style={{ color: 'rgba(255,255,255,0.85)', letterSpacing: '0.02em' }}
                        data-testid={`guest-landing-counter-${feature}`}
                      >
                        {counterText}
                      </span>
                    )}
                  </div>
                  <CaretRight size={18} weight="bold" className="opacity-80" />
                  {isNew && (
                    <span
                      className="absolute -top-1 -right-1 px-2 py-0.5 rounded-full text-[9px] font-black bg-rose-400 text-white ring-2 ring-[#0F1014]"
                      data-testid={`guest-new-pip-${feature}`}
                    >NEW</span>
                  )}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* UPLOAD PANEL — always visible (no tab gating any more). */}
      <>
      {/* PRIMARY UPLOAD CTA */}
      <div className="max-w-md mx-auto mb-6" data-testid="guest-tab-panel-upload">
        {!uploaded ? (
          <p className="text-center text-white/70 text-sm mb-3">
            Capture the day 📸
          </p>
        ) : (
          <div className="rounded-2xl p-4 mb-4 bg-emerald-400/10 border border-emerald-400/30 text-center" data-testid="upload-success-state">
            <CheckCircle size={22} weight="fill" className="mx-auto mb-2 text-emerald-300" />
            <p className="font-black text-white text-sm mb-0.5">Uploaded 🎉</p>
            <p className="text-white/60 text-xs">Your photo is now part of {event.event_name}'s story</p>
          </div>
        )}

        {showPushAsk && (
          <div className="rounded-2xl p-4 mb-4 bg-[#B89762]/40/10 border border-[#B89762]/45/30 flex items-start gap-3" data-testid="guest-push-soft-ask">
            <Bell size={22} weight="fill" className="text-[#C29D52] shrink-0 mt-0.5" />
            <div className="flex-1 min-w-0">
              <p className="font-black text-white text-sm mb-0.5">Get notified of new photos 🔔</p>
              <p className="text-white/60 text-xs mb-2.5">We'll ping you when new memories are added</p>
              <div className="flex items-center gap-2">
                <button
                  onClick={handlePushAsk}
                  className="px-3.5 py-1.5 rounded-lg bg-[#B89762]/40 text-slate-900 text-xs font-black active:scale-95"
                  data-testid="guest-push-ask-yes"
                >Enable</button>
                <button
                  onClick={dismissPushAsk}
                  className="px-3 py-1.5 rounded-lg text-white/50 text-xs font-bold hover:text-white/80"
                  data-testid="guest-push-ask-no"
                >Not now</button>
              </div>
            </div>
            <button onClick={dismissPushAsk} className="text-white/40 hover:text-white/70" aria-label="Dismiss" data-testid="guest-push-ask-close">
              <X size={16} weight="bold" />
            </button>
          </div>
        )}
        <button
          onClick={() => fileRef.current?.click()}
          disabled={uploading}
          className="w-full py-5 rounded-2xl font-black text-white text-base flex items-center justify-center gap-2.5 transition-all active:scale-[0.98] disabled:opacity-60"
          style={{
            background: 'linear-gradient(135deg, #B89762, #C29D52)',
            boxShadow: '0 12px 36px rgba(108,92,231,0.25)',
          }}
          data-testid="upload-photos-btn"
        >
          {uploading ? (
            <>
              <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent" />
              Uploading…
            </>
          ) : (
            <>
              <UploadSimple size={22} weight="bold" />
              {uploaded ? 'Upload more photos' : 'Upload your photos'}
            </>
          )}
        </button>
        <input
          ref={fileRef}
          type="file"
          accept="image/*,video/*"
          multiple
          className="hidden"
          onChange={handleUpload}
          data-testid="upload-input"
        />

        {/* Optional identity (post-action, never required) */}
        {uploaded && !guestName && !showNameFld && (
          <button
            onClick={() => setShowNameFld(true)}
            className="mt-3 text-xs text-white/40 hover:text-white/70 mx-auto block"
            data-testid="add-name-cta"
          >
            Add your name (optional)
          </button>
        )}
        {uploaded && guestName && (
          <p className="mt-2 text-xs text-white/40 text-center">
            Signed as <strong className="text-white/70">{guestName}</strong>
            <button onClick={() => setShowNameFld(true)} className="ml-2 underline opacity-70" data-testid="edit-name-cta">edit</button>
          </p>
        )}
        {showNameFld && (
          <GuestNameField initial={guestName} onSave={saveGuestName} onCancel={() => setShowNameFld(false)} />
        )}
      </div>

      {/* LIVE GALLERY — scoped to the Upload tab so other tabs get a
          clean feature-focused surface. */}
      {photos.length > 0 && (
        <div className="max-w-md mx-auto mb-6" data-testid="guest-live-gallery">
          <div className="flex items-center justify-between mb-2">
            <p className="text-[10px] font-black tracking-[2px] uppercase text-white/50 flex items-center gap-1.5">
              <span className="inline-block w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
              Live gallery
            </p>
            <p className="text-[10px] font-bold text-white/40">{photos.length} moment{photos.length === 1 ? '' : 's'}</p>
          </div>
          <div className="grid grid-cols-3 gap-1.5">
            {photos.map((p) => (
              <div
                key={p.photo_id}
                className="aspect-square rounded-lg bg-white/[0.04] bg-cover bg-center border border-white/5"
                style={{ backgroundImage: `url(${p.url || p.thumbnail_url})` }}
                data-testid={`gallery-tile-${p.photo_id}`}
              />
            ))}
          </div>
        </div>
      )}
      </>

      {/* GUEST → MARKETPLACE BRIDGE — sits between the upload panel +
          live gallery and the feature launchers. Only renders once
          the guest has completed ≥1 experience — otherwise it feels
          random. */}
      {completionStats(slug, event.features || []).completed >= 1 && (
        <SoftMarketplaceCTA eventId={event.event_id} onOpen={openBrowse} />
      )}

      {/* SOFT HOST-CONVERSION CTA — only after upload or 15s dwell */}
      {showHostCta && (
        <div className="max-w-md mx-auto mt-8 p-5 rounded-2xl border border-white/10 text-center" style={{ background: 'linear-gradient(135deg, rgba(184,151,98,0.15), rgba(194,157,82,0.06))' }} data-testid="host-conversion-cta">
          <Sparkle size={22} weight="fill" className="mx-auto mb-2 text-[#C29D52]" />
          <p className="text-white font-black text-sm mb-1">Planning something yourself?</p>
          <p className="text-white/55 text-xs mb-3 leading-relaxed">
            Capture your own event like this. Free to create.
          </p>
          <button
            onClick={() => {
              const params = new URLSearchParams();
              params.set('source', 'guest_conversion');
              if (event?.event_id) params.set('source_event_id', event.event_id);
              navigate(`/create-event?${params.toString()}`);
            }}
            className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-xl text-xs font-black text-white"
            style={{ background: 'linear-gradient(135deg, #B89762, #C29D52)' }}
            data-testid="create-your-event-btn"
          >
            Create your event
            <CaretRight size={11} weight="bold" />
          </button>
        </div>
      )}

      {/* SOFT FAYRE-DISCOVERY CTA — paired with the host CTA. Same
         emotionally-warm trigger (post-upload), but routes high-intent
         couples to the fayres feed instead of the create-event form.
         These two cards complement: planning their own event = create,
         shopping for ideas/suppliers = fayres. */}
      {showHostCta && (
        <div className="max-w-md mx-auto mt-3 p-4 rounded-2xl border border-orange-300/20 text-center" style={{ background: 'linear-gradient(135deg, rgba(255,140,0,0.10), rgba(255,200,80,0.04))' }} data-testid="guest-fayre-cta">
          <p className="text-2xl mb-1.5">🎪</p>
          <p className="text-white font-black text-sm mb-1">Planning your own wedding?</p>
          <p className="text-white/55 text-xs mb-3 leading-relaxed">
            See upcoming wedding fayres near you — meet suppliers in person.
          </p>
          <button
            onClick={() => navigate('/fayres')}
            className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-xl text-xs font-black text-white"
            style={{ background: 'linear-gradient(135deg, #ff8a3d, #ffc05c)' }}
            data-testid="guest-fayre-cta-btn"
          >
            See fayres near you
            <CaretRight size={11} weight="bold" />
          </button>
        </div>
      )}

      {/* Footer */}
      <div className="text-center mt-10">
        <p className="text-white/20 text-[11px]">Powered by EventDrop</p>
      </div>

      {/* Supplier browse layer — slide-up marketplace bridge. Mounted
          at the GuestLanding root so it overlays the entire page when
          ?browse=1 is in the URL. */}
      <SupplierBrowseLayer isOpen={browseOpen} onClose={closeBrowse} event={event} />

      {showKeepMoments && !captured && (
        <KeepMomentsSheet
          eventId={event.event_id}
          eventName={event.event_name}
          guestName={guestName}
          uploads={uploadsOnDevice}
          onClose={() => { setDismissedKeep(true); setShowKeepMoments(false); }}
          onCaptured={() => {
            try { localStorage.setItem(`captured_${slug}`, '1'); } catch {}
            setCaptured(true);
            setShowKeepMoments(false);
          }}
        />
      )}
    </div>
  );
};

// Feature panel — renders per-tab CTAs that deep-link into the
// dedicated feature pages. Uses the shared FEATURE_CARDS + ICON_MAP
// so GuestLanding and GuestHome stay in lockstep.
const FeaturePanel = ({ tab, features, slug, navigate, engagementCounts }) => {
  const visibleFeatures = features.filter((f) => FEATURE_TAB_MAP[f] === tab);
  if (!visibleFeatures.length) return null;
  return (
    <div className="max-w-md mx-auto mb-6 space-y-3" data-testid={`guest-tab-panel-${tab}`}>
      {visibleFeatures.map((feature) => {
        const card = FEATURE_CARDS[feature];
        if (!card) return null;
        const Icon = ICON_MAP[card.iconName] || Camera;
        const counterText = engagementCounterText(feature, engagementCounts);
        return (
          <button
            key={feature}
            onClick={() => navigate(card.route(slug))}
            className="w-full p-5 rounded-2xl border border-white/10 text-left flex items-start gap-4 hover:border-white/25 transition-all active:scale-[0.99]"
            style={{ background: 'linear-gradient(180deg, rgba(255,255,255,0.03), rgba(255,255,255,0.01))' }}
            data-testid={`guest-feature-card-${feature}`}
          >
            <div className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: card.gradient }}>
              <Icon size={22} weight="bold" className="text-white" />
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="text-white font-black text-sm mb-0.5">{card.title}</h3>
              <p className="text-white/55 text-xs leading-snug mb-2">{card.copy}</p>
              <span className="inline-flex items-center gap-1 text-[#C29D52] text-xs font-black">
                {card.cta}
                <CaretRight size={11} weight="bold" />
              </span>
              {counterText && (
                <span
                  className="block text-[10px] font-semibold mt-1.5 text-amber-300/90"
                  style={{ letterSpacing: '0.02em' }}
                  data-testid={`guest-landing-counter-${feature}`}
                >
                  {counterText}
                </span>
              )}
            </div>
          </button>
        );
      })}
    </div>
  );
};

const KeepMomentsSheet = ({ eventId, eventName, guestName, uploads, onClose, onCaptured }) => {
  const [email, setEmail] = useState('');
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const submit = async () => {
    const e = email.trim().toLowerCase();
    if (!e.includes('@')) { toast.error('Enter a valid email'); return; }
    setSaving(true);
    try {
      await axios.post(`${API}/events/${eventId}/guest-interest`, {
        email: e,
        guest_name: guestName || undefined,
        upload_count: uploads,
      });
      setSaved(true);
      toast.success('Saved ✨');
      setTimeout(() => onCaptured?.(), 1200);
    } catch { toast.error('Could not save — try again?'); }
    finally { setSaving(false); }
  };
  return (
    <div className="fixed inset-0 z-[110] flex items-end sm:items-center justify-center bg-black/70 backdrop-blur-sm" onClick={onClose} data-testid="keep-moments-sheet">
      <div className="w-full sm:max-w-sm rounded-t-3xl sm:rounded-2xl p-5 bg-[#15161B] border border-white/10" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between mb-3">
          <div>
            <p className="text-[10px] font-black uppercase tracking-[2px] text-[#C29D52]/85">✨ Memento</p>
            <h3 className="text-lg font-black text-white">Keep your moments forever</h3>
          </div>
          <button onClick={onClose} className="w-7 h-7 rounded-full bg-white/5 hover:bg-white/10 flex items-center justify-center" data-testid="keep-moments-close">
            <X size={12} weight="bold" />
          </button>
        </div>
        {saved ? (
          <div className="text-center py-4" data-testid="keep-moments-saved">
            <Sparkle size={22} weight="fill" className="mx-auto mb-2 text-[#C29D52]" />
            <p className="text-white font-black text-sm mb-1">We'll send you your photos after the event 🎉</p>
            <p className="text-white/50 text-xs">Create your own event anytime</p>
          </div>
        ) : (
          <>
            <p className="text-white/55 text-xs mb-4 leading-relaxed">
              Get a link to everything you've captured from <strong className="text-white/80">{eventName}</strong>.
            </p>
            <div className="flex items-center gap-2 bg-white/[0.05] border border-white/10 rounded-xl px-3 py-2.5 mb-3">
              <Envelope size={14} weight="bold" className="text-white/50" />
              <input
                value={email}
                onChange={(ev) => setEmail(ev.target.value)}
                placeholder="you@example.com"
                type="email"
                inputMode="email"
                autoComplete="email"
                className="flex-1 bg-transparent outline-none text-sm text-white placeholder-white/30"
                data-testid="keep-moments-email-input"
                onKeyDown={(e) => e.key === 'Enter' && submit()}
              />
            </div>
            <button
              onClick={submit}
              disabled={saving}
              className="w-full py-3 rounded-xl text-sm font-black text-white disabled:opacity-60"
              style={{ background: 'linear-gradient(135deg, #B89762, #C29D52)' }}
              data-testid="keep-moments-submit"
            >
              {saving ? 'Saving…' : 'Add your email'}
            </button>
            <p className="text-[10px] text-white/35 text-center mt-3">Optional · no spam · one email after the event</p>
          </>
        )}
      </div>
    </div>
  );
};

const GuestNameField = ({ initial, onSave, onCancel }) => {
  const [v, setV] = useState(initial || '');
  return (
    <div className="mt-3 flex gap-2" data-testid="guest-name-field">
      <input
        autoFocus
        value={v}
        onChange={(e) => setV(e.target.value)}
        placeholder="e.g. Emma's friend"
        className="flex-1 bg-white/[0.05] border border-white/10 rounded-lg px-3 py-2 text-sm text-white outline-none focus:border-[#B89762]/40/40"
        maxLength={40}
      />
      <button
        onClick={() => onSave(v)}
        className="px-3 py-2 rounded-lg text-xs font-bold text-white"
        style={{ background: 'linear-gradient(135deg, #B89762, #C29D52)' }}
      >
        Save
      </button>
      {initial && (
        <button
          onClick={onCancel}
          className="px-2 py-2 rounded-lg text-xs text-white/40 hover:text-white/80"
        >
          <Camera size={12} weight="bold" />
        </button>
      )}
    </div>
  );
};

export default GuestLanding;
